import { formatDate } from '../utils/dateConverter';
import { getCompanyClosures } from '../services/CompanyClosureService';
import { closedDaysInSpan, closureDayMap, listDays } from '../utils/closureDays';
import { extractApiError } from '../utils/apiError';
import React, {useEffect, useMemo, useState} from 'react';
import {getCurrentUserId, getCurrentUserRole} from '../utils/authHelper';
import AbsenceCalendar from '../components/AbsenceCalendar';
import {
    createHoliday,
    deleteHoliday as removeHoliday,
    getHolidayEntries,
    updateHoliday,
} from '../services/HolidayService';
import {getProjectStats} from '../services/ProjectServices';
import {getOvertimeBalances, getTotalOvertimeBalance} from '../services/OvertimeAccountService';
import {
    createSickDay,
    deleteSickDay as removeSickDay,
    getSickEntries,
    updateSickDay
} from '../services/SickDayService';
import {
    createFlexDay,
    deleteFlexDay as removeFlexDay,
    getFlexDayEntries,
    updateFlexDay,
} from '../services/FlexDayService';
import { getOvertimeApprovals } from '../services/OvertimeApprovalService';
import '../styles/EmployeeArea.css';

import {getCurrentMonthRange} from "../utils/dateHandler";
import {useToast, useConfirm} from '../context/UIContext';
import {useSearchParams} from 'react-router-dom';

const EA_TABS = ['holiday', 'sick', 'flexday', 'overtime'];

export default function EmployeeArea() {
    const toast = useToast();
    const confirm = useConfirm();
    const [holidayEntries, setHolidayEntries] = useState([]);
    const [sickEntries, setSickEntries] = useState([]);
    const [flexDayEntries, setFlexDayEntries] = useState([]);
    const [overtimeEntries, setOvertimeEntries] = useState([]);
    const [showAllHolidays, setShowAllHolidays] = useState(false);
    const [showAllSick, setShowAllSick] = useState(false);
    const [showAllFlexDay, setShowAllFlexDay] = useState(false);
    const [showAllOvertime, setShowAllOvertime] = useState(false);

const EA_PAGE_SIZE = 20;

    const [holidayDateStart, setHolidayDateStart] = useState('');
    const [holidayDateEnd, setHolidayDateEnd] = useState('');
    const [holidayNumberDays, setHolidayNumberDays] = useState('');

    const [sickDateStart, setSickDateStart] = useState('');
    const [sickDateEnd, setSickDateEnd] = useState('');
    const [sickDayNumberDays, setSickDayNumberDays] = useState('');

    const [editingHolidayId, setEditingHolidayId] = useState(null);
    const [editingSickId, setEditingSickId] = useState(null);
    const [editingFlexDayId, setEditingFlexDayId] = useState(null);
    // Name of the employee whose flex day is being edited — null when it is one's own.
    // An admin edits other people's entries here, so the form has to say whose it is.
    const [editingFlexDayFor, setEditingFlexDayFor] = useState(null);

    const [flexDayDateStart, setFlexDayDateStart] = useState('');
    const [flexDayDateEnd, setFlexDayDateEnd] = useState('');
    const [flexDayNumberDays, setFlexDayNumberDays] = useState('');

    const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);
    const [closedDays, setClosedDays] = useState(() => new Map());

    const refreshAfterChange = async () => {
        await loadEntries();
        setCalendarRefreshKey(k => k + 1);
    };

    // Closed days are needed before the form is submitted, so the request can be refused
    // without a round trip and the days can be named.
    useEffect(() => {
        let cancelled = false;
        getCompanyClosures()
            .then(list => {
                if (!cancelled) {
                    setClosedDays(closureDayMap(list, isAdmin ? null : userId));
                }
            })
            .catch(() => { if (!cancelled) setClosedDays(new Map()); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [calendarRefreshKey]);

    // `?tab=` is read once as the initial value and deliberately not mirrored back: a deep link
    // that kept re-asserting itself would snap back whenever another tab was clicked.
    const [searchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState(
        () => (EA_TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'holiday'),
    );

    const userId = getCurrentUserId();
    const role = getCurrentUserRole();
    const isAdmin = role === 'admin' || role === 'super_admin';


    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            const [holidays, sickDays, gleitzeit, overtime] = await Promise.allSettled([
                getHolidayEntries({role, userId: userId}),
                getSickEntries({role, userId: userId}),
                getFlexDayEntries({role, userId: userId}),
                getOvertimeApprovals({role, userId: userId}),
                getOvertimeBalances({role, userId: userId}),
                getTotalOvertimeBalance({userId: userId}),
            ]);
            if (cancelled) return;
            if (holidays.status === 'fulfilled') {
                setHolidayEntries(holidays.value);
                const map = {};
                holidays.value.forEach(h => { map[String(h.id)] = h.approvalStatus; });
                localStorage.setItem('holidayStatusMap', JSON.stringify(map));
            }
            if (sickDays.status === 'fulfilled') setSickEntries(sickDays.value);
            if (gleitzeit.status === 'fulfilled') setFlexDayEntries(gleitzeit.value);
            if (overtime.status === 'fulfilled') setOvertimeEntries(overtime.value);
        };
        void run();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const loadEntries = async () => {
        const [holidays, sickDays, gleitzeit, overtime] = await Promise.allSettled([
            getHolidayEntries({role, userId: userId}),
            getSickEntries({role, userId: userId}),
            getFlexDayEntries({role, userId: userId}),
            getOvertimeApprovals({role, userId: userId}),
            getOvertimeBalances({role, userId: userId}),
            getTotalOvertimeBalance({userId: userId}),
        ]);
        if (holidays.status === 'fulfilled') {
            setHolidayEntries(holidays.value);
            const map = {};
            holidays.value.forEach(h => { map[String(h.id)] = h.approvalStatus; });
            localStorage.setItem('holidayStatusMap', JSON.stringify(map));
        }
        if (sickDays.status === 'fulfilled') setSickEntries(sickDays.value);
        if (gleitzeit.status === 'fulfilled') setFlexDayEntries(gleitzeit.value);
        if (overtime.status === 'fulfilled') setOvertimeEntries(overtime.value);
    };

    const fmtMinutes = (m) => {
        const sign = m < 0 ? '-' : '';
        const abs = Math.abs(m);
        return `${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;
    };

    const renderApprovalStatus = (e) => (
        <>
            <span className={`approval-badge approval-badge--${e.approvalStatus}`}>
                {e.approvalStatus === 'approved' ? 'Genehmigt'
                    : e.approvalStatus === 'rejected' ? 'Abgelehnt'
                        : 'Ausstehend'}
            </span>
            {e.approvalStatus === 'rejected' && e.rejectionReason && (
                <div className="approval-reason" title={e.rejectionReason}>
                    {e.rejectionReason}
                </div>
            )}
        </>
    );


    const saveHoliday = async () => {
        if (!holidayDateStart || !holidayDateEnd || !holidayNumberDays) {
            toast('Bitte alle Felder ausfüllen (Von, Bis, Anzahl Tage).', 'error');
            return;
        }
        if (holidayDateEnd < holidayDateStart) {
            toast('Das Enddatum muss nach dem Startdatum liegen.', 'error');
            return;
        }
        if (parseInt(holidayNumberDays) <= 0) {
            toast('Ungültige Anzahl Tage.', 'error');
            return;
        }
        // The API refuses this too; checking here names the days and saves the round trip.
        const closed = closedDaysInSpan(holidayDateStart, holidayDateEnd, closedDays);
        if (closed.length > 0) {
            toast(
                `An diesen Tagen ist bereits Betriebsschließung, es wird kein Urlaub benötigt: ${listDays(closed)}`,
                'error',
            );
            return;
        }

        try {
            if (editingHolidayId) {
                await updateHoliday(editingHolidayId, {
                    user_id: userId,
                    start_date: holidayDateStart,
                    end_date: holidayDateEnd,
                    number_days: holidayNumberDays
                });
            } else {
                await createHoliday({
                    start_date: holidayDateStart,
                    end_date: holidayDateEnd,
                    number_days: holidayNumberDays,
                    user_id: userId
                });
            }

            setHolidayDateStart('');
            setHolidayDateEnd('');
            setHolidayNumberDays('');
            setEditingHolidayId(null);
            toast(editingHolidayId ? 'Urlaub aktualisiert.' : 'Urlaub eingetragen.', 'success');
            await refreshAfterChange();
        } catch (err) {
            console.error('Failed saving holiday', err);
            toast(extractApiError(err, 'Fehler beim Speichern des Urlaubs.'), 'error');
        }
    };

    const saveSickDay = async () => {
        if (!sickDateStart || !sickDateEnd || !sickDayNumberDays) {
            toast('Bitte alle Felder ausfüllen (Von, Bis, Anzahl Tage).', 'error');
            return;
        }
        if (sickDateEnd < sickDateStart) {
            toast('Das Enddatum muss nach dem Startdatum liegen.', 'error');
            return;
        }
        if (parseInt(sickDayNumberDays) <= 0) {
            toast('Ungültige Anzahl Tage.', 'error');
            return;
        }

        try {
            if (editingSickId) {
                await updateSickDay(editingSickId, {
                    user_id: userId,
                    start_date: sickDateStart,
                    end_date: sickDateEnd,
                    number_days: sickDayNumberDays
                });
            } else {
                await createSickDay({
                    user_id: userId,
                    start_date: sickDateStart,
                    end_date: sickDateEnd,
                    number_days: sickDayNumberDays
                });
            }

            setSickDateStart('');
            setSickDateEnd('');
            setSickDayNumberDays('');
            setEditingSickId(null);
            toast(editingSickId ? 'Krankheitstag aktualisiert.' : 'Krankheitstag eingetragen.', 'success');
            await refreshAfterChange();
        } catch (err) {
            console.error('Failed saving sick day', err);
            toast('Fehler beim Speichern des Krankheitstags.', 'error');
        }
    };

    const deleteHoliday = async (id) => {
        if (await confirm('Urlaub löschen?', {confirmLabel: 'Löschen', danger: true})) {
            try {
                await removeHoliday(id);
                toast('Urlaub gelöscht.', 'success');
                await refreshAfterChange();
            } catch (err) {
                console.error('Failed deleting holiday', err);
                toast('Fehler beim Löschen des Urlaubs.', 'error');
            }
        }
    };


    const deleteSickDay = async (id) => {
        if (await confirm('Krankheitstag löschen?', {confirmLabel: 'Löschen', danger: true})) {
            try {
                await removeSickDay(id);
                toast('Krankheitstag gelöscht.', 'success');
                await refreshAfterChange();
            } catch (err) {
                console.error('Failed deleting sick day', err);
                toast('Fehler beim Löschen des Krankheitstags.', 'error');
            }
        }
    };

    /** Load a flex day into the form. Admins may do this for any employee's entry. */
    const startEditFlexDay = (e) => {
        setFlexDayDateStart((e.startDate || '').slice(0, 10));
        setFlexDayDateEnd((e.endDate || '').slice(0, 10));
        setFlexDayNumberDays(e.numberDays ?? '');
        setEditingFlexDayId(e.id);
        setEditingFlexDayFor(e.userId === userId ? null : (e.displayName || 'Mitarbeiter'));
    };

    const resetFlexDayForm = () => {
        setFlexDayDateStart('');
        setFlexDayDateEnd('');
        setFlexDayNumberDays('');
        setEditingFlexDayId(null);
        setEditingFlexDayFor(null);
    };

    const saveGleitzeit = async () => {
        if (!flexDayDateStart || !flexDayDateEnd || !flexDayNumberDays) {
            toast('Bitte alle Felder ausfüllen (Von, Bis, Anzahl Tage).', 'error');
            return;
        }
        if (flexDayDateEnd < flexDayDateStart) {
            toast('Das Enddatum muss nach dem Startdatum liegen.', 'error');
            return;
        }
        if (parseInt(flexDayNumberDays) <= 0) {
            toast('Ungültige Anzahl Tage.', 'error');
            return;
        }
        try {
            if (editingFlexDayId) {
                await updateFlexDay(editingFlexDayId, {
                    start_date: flexDayDateStart,
                    end_date: flexDayDateEnd,
                    number_days: flexDayNumberDays,
                });
            } else {
                await createFlexDay({
                    user_id: userId,
                    start_date: flexDayDateStart,
                    end_date: flexDayDateEnd,
                    number_days: flexDayNumberDays,
                });
            }
            const wasEditing = editingFlexDayId;
            resetFlexDayForm();
            toast(wasEditing ? 'Flex Day aktualisiert.' : 'Flex Day eingetragen.', 'success');
            await refreshAfterChange();
        } catch (err) {
            console.error('Failed saving Gleitzeit', err);
            toast('Fehler beim Speichern des Flex Days.', 'error');
        }
    };

    const deleteFlexDay = async (id) => {
        if (await confirm('Flex Day löschen?', {confirmLabel: 'Löschen', danger: true})) {
            try {
                await removeFlexDay(id);
                toast('Flex Day gelöscht.', 'success');
                await refreshAfterChange();
            } catch (err) {
                console.error('Failed deleting Gleitzeit', err);
                toast('Fehler beim Löschen des Flex Days.', 'error');
            }
        }
    };


    return (
        <div className="employee-area-wrapper">
            <div className="employee-area-content">
                <h2>Abwesenheiten</h2>


                <div className="internal-nav">
                    <button
                        className={`internal-tab ${activeTab === 'holiday' ? 'active' : ''}`}
                        onClick={() => setActiveTab('holiday')}>
                        Urlaub
                    </button>
                    <button
                        className={`internal-tab ${activeTab === 'sick' ? 'active' : ''}`}
                        onClick={() => setActiveTab('sick')}>
                        Krankheitstag
                    </button>
                    <button
                        className={`internal-tab ${activeTab === 'flexday' ? 'active' : ''}`}
                        onClick={() => setActiveTab('flexday')}>
                        Flex Day
                    </button>
                    <button
                        className={`internal-tab ${activeTab === 'overtime' ? 'active' : ''}`}
                        onClick={() => setActiveTab('overtime')}>
                        Überstunden
                    </button>
                </div>

                {activeTab === 'holiday' && (
                    <div>
                        <h3>Urlaub eintragen</h3>

                        <div style={{display: 'flex', gap: '8px', alignItems: 'flex-end', width: '100%'}}>
                            <div style={{flex: 1}}>
                                <label style={{display: 'block', fontSize: '0.8rem', marginBottom: '2px'}}>Von</label>
                                <input
                                    type="date"
                                    value={holidayDateStart}
                                    onChange={e => setHolidayDateStart(e.target.value)}
                                    className="input"
                                    style={{width: '100%'}}/>
                            </div>
                            <div style={{flex: 1}}>
                                <label style={{display: 'block', fontSize: '0.8rem', marginBottom: '2px'}}>Bis</label>
                                <input
                                    type="date"
                                    value={holidayDateEnd}
                                    onChange={e => setHolidayDateEnd(e.target.value)}
                                    className="input"
                                    style={{width: '100%'}}/>
                            </div>
                            <div style={{flex: 1}}>
                                <label style={{display: 'block', fontSize: '0.8rem', marginBottom: '2px'}}>Anzahl
                                    Tage</label>
                                <input
                                    type="number"
                                    value={holidayNumberDays}
                                    onChange={e => setHolidayNumberDays(e.target.value)}
                                    className="input"
                                    min="1"
                                    step="1"
                                    placeholder="z.B. 5"
                                    style={{width: '100%'}}/>
                            </div>
                            <button
                                onClick={saveHoliday}
                                className="employee_area_btn"
                                style={{flex: 1}}>
                                {editingHolidayId ? 'Aktualisieren' : 'Eintragen'}
                            </button>
                        </div>

                        {role !== 'admin' && role !== 'super_admin' && (
                            <p style={{
                                fontSize: '0.8rem',
                                color: 'var(--color-text-muted)',
                                marginTop: 'var(--space-2)'
                            }}>
                                Eingetragene Urlaube müssen erst vom Vorgesetzen genehmigt werden und erscheinen bis dahin als
                                „Ausstehend".
                            </p>
                        )}

                        <div className="table-scroll">
                            <table className="holiday-table table--cards">
                                <thead>
                                <tr>
                                    <th>Startdatum</th>
                                    <th>Enddatum</th>
                                    <th>Urlaubstage</th>
                                    {(role === 'admin' || role === 'super_admin') && <th>Mitarbeiter</th>}
                                    <th>Status</th>
                                    <th>Aktion</th>
                                </tr>
                                </thead>
                                <tbody>
                                {(showAllHolidays
                                        ? holidayEntries.sort((a, b) => new Date(b.startDate) - new Date(a.startDate))
                                        : holidayEntries.sort((a, b) => new Date(b.startDate) - new Date(a.startDate)).slice(0, EA_PAGE_SIZE)
                                ).map(e => (
                                    <tr key={e.id}>
                                        <td data-label="Startdatum">{formatDate(e.startDate)}</td>
                                        <td data-label="Enddatum">{formatDate(e.endDate)}</td>
                                        <td data-label="Urlaubstage">{e.numberDays}</td>
                                        {(role === 'admin' || role === 'super_admin') && <td data-label="Mitarbeiter">{e.displayName || '—'}</td>}
                                        <td data-label="Status">{renderApprovalStatus(e)}</td>
                                        <td className="table-card-actions">
                                            {e.approvalStatus === 'pending' && e.userId === userId && (
                                                <button className={"button-warning"} onClick={() => {
                                                    setHolidayDateStart((e.startDate || '').slice(0, 10));
                                                    setHolidayDateEnd((e.endDate || '').slice(0, 10));
                                                    setHolidayNumberDays(e.numberDays ?? '');
                                                    setEditingHolidayId(e.id);
                                                }}>Bearbeiten
                                                </button>
                                            )}
                                            {((role === 'admin' || role === 'super_admin') || e.approvalStatus === 'pending') && (
                                                <button className={"button-danger"}
                                                        onClick={() => deleteHoliday(e.id)}>Löschen
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                        {holidayEntries.length > EA_PAGE_SIZE && (
                            <button className="button" style={{marginTop: 'var(--space-3)'}}
                                    onClick={() => setShowAllHolidays(v => !v)}>
                                {showAllHolidays ? 'Weniger anzeigen' : `Alle anzeigen (${holidayEntries.length})`}
                            </button>
                        )}
                    </div>
                )}

                {activeTab === 'sick' && (
                    <div>
                        <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginBottom: 'var(--space-2)' }}>
                            Krankheitstage erfordern keine Genehmigung und können jederzeit bearbeitet oder gelöscht werden.
                        </p>
                        <div className="section">
                            <h3>Krankheitstag eintragen</h3>
                            <div style={{display: 'flex', gap: '8px', alignItems: 'flex-end'}}>
                                <div style={{flex: 1}}>
                                    <label
                                        style={{display: 'block', fontSize: '0.8rem', marginBottom: '2px'}}>Von</label>
                                    <input
                                        type="date"
                                        className="input"
                                        value={sickDateStart}
                                        onChange={e => setSickDateStart(e.target.value)}
                                        style={{width: '100%'}}
                                    />
                                </div>
                                <div style={{flex: 1}}>
                                    <label
                                        style={{display: 'block', fontSize: '0.8rem', marginBottom: '2px'}}>Bis</label>
                                    <input
                                        type="date"
                                        className="input"
                                        value={sickDateEnd}
                                        onChange={e => setSickDateEnd(e.target.value)}
                                        style={{width: '100%'}}
                                    />
                                </div>
                                <div style={{flex: 1}}>
                                    <label style={{display: 'block', fontSize: '0.8rem', marginBottom: '2px'}}>Anzahl
                                        Tage</label>
                                    <input
                                        type="number"
                                        value={sickDayNumberDays}
                                        onChange={e => setSickDayNumberDays(e.target.value)}
                                        className="input"
                                        min="1"
                                        step="1"
                                        placeholder="z.B. 3"
                                        style={{width: '100%'}}/>
                                </div>
                                <button
                                    className="employee_area_btn"
                                    onClick={saveSickDay}
                                    style={{flex: 1}}
                                >
                                    {editingSickId ? 'Aktualisieren' : 'Eintragen'}
                                </button>
                            </div>
                        </div>

                        <div className="table-scroll">
                            <table className="holiday-table table--cards">
                                <thead>
                                <tr>
                                    <th>Startdatum</th>
                                    <th>Enddatum</th>
                                    <th>Krankheitstage</th>
                                    {role === 'admin' && <th>Mitarbeiter</th>}
                                    <th>Aktion</th>
                                </tr>
                                </thead>
                                <tbody>
                                {(showAllSick
                                        ? sickEntries.filter(e => role === 'admin' || role === 'super_admin' || e.userId === userId).sort((a, b) => new Date(b.startDate) - new Date(a.startDate))
                                        : sickEntries.filter(e => role === 'admin' || role === 'super_admin' || e.userId === userId).sort((a, b) => new Date(b.startDate) - new Date(a.startDate)).slice(0, EA_PAGE_SIZE)
                                ).map(e => (
                                    <tr key={e.id}>
                                        <td data-label="Startdatum">{formatDate(e.startDate)}</td>
                                        <td data-label="Enddatum">{formatDate(e.endDate)}</td>
                                        <td data-label="Krankheitstage">{e.numberDays}</td>
                                        {role === 'admin' && <td data-label="Mitarbeiter">{e.displayName}</td>}
                                        <td className="table-card-actions">
                                            <button
                                                className={"button-warning"}
                                                onClick={() => {
                                                    setSickDateStart((e.startDate || '').slice(0, 10));
                                                    setSickDateEnd((e.endDate || '').slice(0, 10));
                                                    setSickDayNumberDays(e.numberDays ?? '');
                                                    setEditingSickId(e.id);
                                                }}
                                            >
                                                Bearbeiten
                                            </button>
                                            <button className={"button-danger"} onClick={() => deleteSickDay(e.id)}>
                                                Löschen
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                        {sickEntries.filter(e => role === 'admin' || role === 'super_admin' || e.userId === userId).length > EA_PAGE_SIZE && (
                            <button className="button" style={{marginTop: 'var(--space-3)'}}
                                    onClick={() => setShowAllSick(v => !v)}>
                                {showAllSick ? 'Weniger anzeigen' : `Alle anzeigen (${sickEntries.filter(e => role === 'admin' || role === 'super_admin' || e.userId === userId).length})`}
                            </button>
                        )}
                    </div>
                )}

                {activeTab === 'flexday' && (
                    <div>
                        <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginBottom: 'var(--space-2)' }}>
                            Flex Days ermöglichen es, angesammelte Überstunden durch einen ganzen freien Tag abzubauen. Eingetragene Gleittage müssen vom Vorgesetzen genehmigt werden.
                        </p>
                        <div className="section">
                            <h3>
                                {!editingFlexDayId ? 'Flex Day eintragen'
                                    : editingFlexDayFor ? `Flex Day von ${editingFlexDayFor} bearbeiten`
                                        : 'Flex Day bearbeiten'}
                            </h3>
                            <div style={{display: 'flex', gap: '8px', alignItems: 'flex-end'}}>
                                <div style={{flex: 1}}>
                                    <label style={{display: 'block', fontSize: '0.8rem', marginBottom: '2px'}}>Von</label>
                                    <input
                                        type="date"
                                        className="input"
                                        value={flexDayDateStart}
                                        onChange={e => setFlexDayDateStart(e.target.value)}
                                        style={{width: '100%'}}
                                    />
                                </div>
                                <div style={{flex: 1}}>
                                    <label style={{display: 'block', fontSize: '0.8rem', marginBottom: '2px'}}>Bis</label>
                                    <input
                                        type="date"
                                        className="input"
                                        value={flexDayDateEnd}
                                        onChange={e => setFlexDayDateEnd(e.target.value)}
                                        style={{width: '100%'}}
                                    />
                                </div>
                                <div style={{flex: 1}}>
                                    <label style={{display: 'block', fontSize: '0.8rem', marginBottom: '2px'}}>Anzahl Tage</label>
                                    <input
                                        type="number"
                                        value={flexDayNumberDays}
                                        onChange={e => setFlexDayNumberDays(e.target.value)}
                                        className="input"
                                        min="1"
                                        step="1"
                                        placeholder="z.B. 1"
                                        style={{width: '100%'}}
                                    />
                                </div>
                                <button
                                    className="employee_area_btn"
                                    onClick={saveGleitzeit}
                                    style={{flex: 1}}
                                >
                                    {editingFlexDayId ? 'Aktualisieren' : 'Eintragen'}
                                </button>
                                {editingFlexDayId && (
                                    <button className="button" onClick={resetFlexDayForm} style={{flex: 1}}>
                                        Abbrechen
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="table-scroll">
                            <table className="holiday-table table--cards">
                                <thead>
                                <tr>
                                    <th>Startdatum</th>
                                    <th>Enddatum</th>
                                    <th>Tage</th>
                                    {(role === 'admin' || role === 'super_admin') && <th>Mitarbeiter</th>}
                                    <th>Status</th>
                                    <th>Aktion</th>
                                </tr>
                                </thead>
                                <tbody>
                                {(showAllFlexDay
                                    ? flexDayEntries.sort((a, b) => new Date(b.startDate) - new Date(a.startDate))
                                    : flexDayEntries.sort((a, b) => new Date(b.startDate) - new Date(a.startDate)).slice(0, EA_PAGE_SIZE)
                                ).map(e => (
                                    <tr key={e.id}>
                                        <td data-label="Startdatum">{formatDate(e.startDate)}</td>
                                        <td data-label="Enddatum">{formatDate(e.endDate)}</td>
                                        <td data-label="Tage">{e.numberDays}</td>
                                        {(role === 'admin' || role === 'super_admin') && <td data-label="Mitarbeiter">{e.displayName || '—'}</td>}
                                        <td data-label="Status">{renderApprovalStatus(e)}</td>
                                        <td className="table-card-actions">
                                            {(isAdmin || (e.approvalStatus === 'pending' && e.userId === userId)) && (
                                                <button className="button-warning"
                                                        onClick={() => startEditFlexDay(e)}>
                                                    Bearbeiten
                                                </button>
                                            )}
                                            {((role === 'admin' || role === 'super_admin') || e.approvalStatus === 'pending') && (
                                                <button className="button-danger" onClick={() => deleteFlexDay(e.id)}>Löschen</button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                        {flexDayEntries.length > EA_PAGE_SIZE && (
                            <button className="button" style={{marginTop: 'var(--space-3)'}}
                                    onClick={() => setShowAllFlexDay(v => !v)}>
                                {showAllFlexDay ? 'Weniger anzeigen' : `Alle anzeigen (${flexDayEntries.length})`}
                            </button>
                        )}
                    </div>
                )}

                {activeTab === 'overtime' && (
                    <div>
                        <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginBottom: 'var(--space-2)' }}>
                            Überstunden werden automatisch erfasst, wenn die tatsächliche Arbeitszeit die vertragliche Sollzeit überschreitet.
                            Sie müssen vom Vorgesetzten genehmigt werden, bevor sie zum Stundenkonto hinzugezählt werden.
                        </p>
                        <div className="table-scroll">
                            <table className="holiday-table table--cards">
                                <thead>
                                    <tr>
                                        <th>Datum</th>
                                        {(role === 'admin' || role === 'super_admin') && <th>Mitarbeiter</th>}
                                        <th>Soll</th>
                                        <th>Ist</th>
                                        <th>Überstunden</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(showAllOvertime
                                        ? overtimeEntries
                                        : overtimeEntries.slice(0, EA_PAGE_SIZE)
                                    ).map(e => (
                                        <tr key={e.id}>
                                            <td data-label="Datum">{formatDate(e.date)}</td>
                                            {(role === 'admin' || role === 'super_admin') && <td data-label="Mitarbeiter">{e.displayName || '—'}</td>}
                                            <td data-label="Soll">{fmtMinutes(e.plannedMinutes)}</td>
                                            <td data-label="Ist">{fmtMinutes(e.actualMinutes)}</td>
                                            <td data-label="Überstunden"><strong>+{fmtMinutes(e.overtimeMinutes)}</strong></td>
                                            <td className="table-card-actions">{renderApprovalStatus(e)}</td>
                                        </tr>
                                    ))}
                                    {overtimeEntries.length === 0 && (
                                        <tr>
                                            <td colSpan={role === 'admin' || role === 'super_admin' ? 6 : 5}
                                                style={{ textAlign: 'center', color: 'var(--color-text-muted)', padding: 'var(--space-4)' }}>
                                                Keine Überstunden erfasst.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {overtimeEntries.length > EA_PAGE_SIZE && (
                            <button className="button" style={{ marginTop: 'var(--space-3)' }}
                                onClick={() => setShowAllOvertime(v => !v)}>
                                {showAllOvertime ? 'Weniger anzeigen' : `Alle anzeigen (${overtimeEntries.length})`}
                            </button>
                        )}
                    </div>
                )}

            </div>

            <div className="employee-area-calendar">
                <AbsenceCalendar key={calendarRefreshKey}/>
            </div>
        </div>
    );
}
